"use client"
import React, { useState, useEffect, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import { type ClassNameValue, twMerge } from 'tailwind-merge';
import {
  type ComponentRegistry,
  markdownComponents,
} from './markdown';
import { type Role } from '@/types';
import { useTextStream } from "@/components/ui/response-stream";
import { Button } from '@/components/ui/button';
import { Copy, Check } from 'lucide-react';
import { copyContentToClipboard, copyAsMarkdown } from '@/utils/copyToClipboard';

interface MessageContentProps {
  content: string;
  isUser: boolean;
  role: Role;
  className?: ClassNameValue;
  additionalComponents?: ComponentRegistry;
  streaming?: boolean;
  streamSpeed?: number;
  streamMode?: "typewriter";
  onStreamComplete?: () => void;
  isCurrentlyStreaming?: boolean;
  isLastMessage?: boolean;
}

// Role-based styling configurations using CSS variables
const roleStyles: Record<Role, { container: string }> = {
  bot: {
    container: 'bg-bot-message rounded-lg border-l-4'
  },
  assistant: {
    container: 'bg-bot-message rounded-lg border-l-4'
  },
  admin: {
    container: 'bg-[var(--chat-admin-bg)] border-l-4 border-[var(--chat-admin)] rounded-lg'
  },
  receiver: {
    container: 'bg-[var(--chat-receiver-bg)] border-l-4 border-[var(--chat-receiver)] rounded-lg'
  },
  user: {
    container: 'bg-user-message rounded-lg border-l-4 text-foreground'
  },
  other: {
    container: 'bg-muted text-muted-foreground rounded-lg border-l-4 border-muted'
  }
};

// Individual chunk error boundary component
const ChunkErrorBoundary: React.FC<{
  children: React.ReactNode;
  index: number;
}> = ({ children, index }) => {
  const [hasError, setHasError] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const target = event.target as Node;
      const boundary = document.getElementById(`chunk-boundary-${index}`);
      if (boundary?.contains(target)) {
        event.preventDefault();
        setHasError(true);
        setError(event.error);
      }
    };

    window.addEventListener('error', handleError, true);
    return () => window.removeEventListener('error', handleError, true);
  }, [index]);

  if (hasError) {
    return (
      <div className="p-2 my-2 text-sm text-red-500 bg-red-50 rounded">
        Error in chunk {index + 1}: {error?.message || 'Failed to render content'}
      </div>
    );
  }

  return <div id={`chunk-boundary-${index}`}>{children}</div>;
};

// Simplified content cleaning function - just remove thinking tags
const cleanContent = (content: string): string => {
  // Remove all thinking tags from content
  const allTagsRegex = /<thinking>[\s\S]*?<\/thinking>/g;
  return content.replace(allTagsRegex, '');
};

// Improved content splitting function
const splitContent = (content: string): string[] => {
  const chunks: string[] = [];
  
  // Components that need to be kept on their own lines (block components)
  // IMPORTANT: When adding new custom UI components (e.g., in src/components/markdown/),
  // you MUST add them to this list. Otherwise, content after the component may be
  // incorrectly processed and disappear, especially when combined with thinking tags.
  // Examples of when to add here:
  // - Self-closing components like <mycomponent />
  // - Components with content like <mycomponent>...</mycomponent>
  const blockComponents = [
    { open: '<networkgraph', close: '/>' },
    { open: '<preview', close: '/>' },
    { open: '<plot', close: '/>' },
    { open: '<googlesearch', close: '</googlesearch>' },
    { open: '<alert', close: '</alert>' },
    { open: '<svg', close: '</svg>' },
    { open: '<toolconfirmation', close: '/>' },
    { open: '<assistantresponse', close: '</assistantresponse>' },
    { open: '<artifact', close: '</artifact>' }
  ];
  
  // Inline components that should not break text flow
  const inlineComponents = [
    { open: '<tooltip', close: '</tooltip>' }
  ];
  
  // Regular expression pattern to match code blocks
  const codeBlockPattern = /```[\s\S]*?```/g;
  
  // Find positions of all block components that should be separated
  const componentPositions: Array<{ start: number; end: number; isBlock: boolean }> = [];
  const MAX_COMPONENTS = 500; // Safety guard to prevent memory issues
  
  // Find positions of code blocks
  let codeMatch;
  let codeBlockCounter = 0;
  const MAX_CODE_BLOCKS = 100; // Safety guard for code blocks
  
  while ((codeMatch = codeBlockPattern.exec(content)) !== null && codeBlockCounter < MAX_CODE_BLOCKS) {
    codeBlockCounter++;
    
    // Safety guard to prevent array from growing too large
    if (componentPositions.length >= MAX_COMPONENTS) break;
    
    componentPositions.push({
      start: codeMatch.index,
      end: codeMatch.index + codeMatch[0].length,
      isBlock: true
    });
  }
  
  // Find positions of block components
  blockComponents.forEach(comp => {
    let startIndex = 0;
    let openTag: number, closeTag: number;
    let loopCounter = 0;
    const MAX_ITERATIONS = 1000; // Safety guard
    
    while (startIndex < content.length && loopCounter < MAX_ITERATIONS) {
      loopCounter++;
      openTag = content.indexOf(comp.open, startIndex);
      if (openTag === -1) break;
      
      // If comp.close is single tag like '/>'
      if (comp.close === '/>') {
        const selfClosePos = content.indexOf('/>', openTag);
        if (selfClosePos === -1) break;
        closeTag = selfClosePos + 2;
      } else {
        // For components with separate closing tags
        closeTag = content.indexOf(comp.close, openTag + comp.open.length);
        if (closeTag === -1) break;
        closeTag += comp.close.length;
      }
      
      // Ensure closeTag is valid and after openTag
      if (closeTag === -1 || closeTag <= openTag) break;
      
      // Safety guard to prevent array from growing too large
      if (componentPositions.length >= MAX_COMPONENTS) break;
      
      componentPositions.push({
        start: openTag,
        end: closeTag,
        isBlock: true
      });
      
      // Ensure we advance past the current component
      startIndex = Math.max(closeTag, openTag + 1);
    }
  });
  
  // Find positions of inline components (we mark them but don't separate them)
  inlineComponents.forEach(comp => {
    let startIndex = 0;
    let openTag: number, closeTag: number;
    let loopCounter = 0;
    const MAX_ITERATIONS = 1000; // Safety guard
    
    while (startIndex < content.length && loopCounter < MAX_ITERATIONS) {
      loopCounter++;
      openTag = content.indexOf(comp.open, startIndex);
      if (openTag === -1) break;
      
      closeTag = content.indexOf(comp.close, openTag + comp.open.length);
      if (closeTag === -1) break;
      closeTag += comp.close.length;
      
      // Ensure closeTag is valid and after openTag
      if (closeTag <= openTag) break;
      
      // Safety guard to prevent array from growing too large
      if (componentPositions.length >= MAX_COMPONENTS) break;
      
      componentPositions.push({
        start: openTag,
        end: closeTag,
        isBlock: false // This is inline, should stay with surrounding text
      });
      
      // Ensure we advance past the current component
      startIndex = Math.max(closeTag, openTag + 1);
    }
  });
  
  // Sort positions by start index
  componentPositions.sort((a, b) => a.start - b.start);
  
  // Filter out overlapping positions (can happen with nested components)
  const filteredPositions: typeof componentPositions = [];
  let lastEnd = -1;
  
  for (const pos of componentPositions) {
    if (pos.start > lastEnd) {
      filteredPositions.push(pos);
      lastEnd = pos.end;
    }
  }
  
  // If no components found, return the whole content
  if (filteredPositions.length === 0) {
    return [content];
  }
  
  // Process content, separating only block components into their own chunks
  lastEnd = 0;
  let currentChunk = '';
  
  for (const pos of filteredPositions) {
    // Add text before this component
    if (pos.start > lastEnd) {
      currentChunk += content.substring(lastEnd, pos.start);
    }
    
    // Get the component itself
    const componentText = content.substring(pos.start, pos.end);
    
    if (pos.isBlock) {
      // If we have accumulated text, add it as a chunk
      if (currentChunk.trim()) {
        chunks.push(currentChunk.trim());
        currentChunk = '';
      }
      
      // Add the block component as its own chunk
      chunks.push(componentText);
    } else {
      // For inline components, just add them to the current chunk
      currentChunk += componentText;
    }
    
    lastEnd = pos.end;
  }
  
  // Add any remaining content after the last component
  if (lastEnd < content.length) {
    currentChunk += content.substring(lastEnd);
  }
  
  // Add the final chunk if it has content
  if (currentChunk.trim()) {
    chunks.push(currentChunk.trim());
  }
  
  // Filter out empty chunks
  return chunks.filter(chunk => chunk.trim().length > 0);
};

// Memoize the role components
const roleComponents = {
  user: markdownComponents,
  bot: markdownComponents,
  assistant: markdownComponents,
  other: markdownComponents,
  receiver: markdownComponents,
  admin: markdownComponents
};

/**
 * A wrapper component that only re-renders when 'content' changes
 * Prevents re-renders when thinking content changes don't affect this content
 */
const StreamingChunk: React.FC<{
  content: string;
  components: any;
  streaming: boolean;
  streamSpeed?: number;
  streamMode?: "typewriter";
  onStreamComplete?: () => void;
  index: number;
  isCurrentlyStreaming?: boolean;
}> = ({
  content,
  components,
  streaming,
  streamSpeed = 30,
  streamMode = "typewriter",
  onStreamComplete,
  index,
  isCurrentlyStreaming = false
}) => {
  // Only use streaming for the current streaming message
  const shouldUseStreaming = streaming && isCurrentlyStreaming;
  
  // Hooks must be called before any conditional returns
  // Use the optimized hook for streaming content
  const { displayedText, startStreaming, isComplete } = useTextStream({
    textStream: content,
    mode: streamMode,
    speed: streamSpeed,
  });

  // Start streaming immediately for the active message
  useEffect(() => {
    if (shouldUseStreaming) {
      startStreaming();
    }
  }, [shouldUseStreaming, startStreaming]);

  useEffect(() => {
    if (isComplete && onStreamComplete && index === 0) {
      onStreamComplete();
    }
  }, [isComplete, onStreamComplete, index]);
  
  // For non-streaming messages or inactive messages, just show content directly
  if (!shouldUseStreaming) {
    return (
      <Markdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={components}
        skipHtml={false}
      >
        {content}
      </Markdown>
    );
  }

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeRaw, rehypeKatex]}
      components={components}
      skipHtml={false}
    >
      {displayedText}
    </Markdown>
  );
};


export const MessageContent: React.FC<MessageContentProps> = ({
  content,
  isUser,
  role,
  className,
  additionalComponents = {},
  streaming = false,
  streamSpeed = 30,
  streamMode = "typewriter",
  onStreamComplete,
  isCurrentlyStreaming = false,
  isLastMessage = false
}) => {
  const [copied, setCopied] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);
  
  // Ensure content is clean of thinking tags (now handled by parent component)
  const cleanedContent = React.useMemo(() => cleanContent(content), [content]);
  
  // Split content into chunks and deduplicate toolconfirmation tags
  const contentChunks = React.useMemo(() => {
    const chunks = splitContent(cleanedContent);
    
    // Keep only the last toolconfirmation chunk (most complete/final one)
    const toolconfirmationIndices: number[] = [];
    chunks.forEach((chunk, index) => {
      if (chunk.toLowerCase().includes('<toolconfirmation')) {
        toolconfirmationIndices.push(index);
      }
    });
    
    return chunks.filter((chunk, index) => {
      if (chunk.toLowerCase().includes('<toolconfirmation')) {
        const isLastToolconfirmation = index === toolconfirmationIndices[toolconfirmationIndices.length - 1];
        if (!isLastToolconfirmation) {
          console.log('[MessageContent] Filtering out earlier toolconfirmation chunk');
          return false;
        }
        console.log('[MessageContent] Keeping last toolconfirmation chunk');
      }
      return true;
    });
  }, [cleanedContent]);

  // Check if we should apply streaming to this message
  const shouldStreamThisMessage = streaming && 
                       (role === 'assistant' || role === 'bot') && 
                       isCurrentlyStreaming;
  
  // Memoize the combined components
  const components = React.useMemo(() => ({
    ...roleComponents[role],
    ...additionalComponents,
    p: ({ children }: { children?: React.ReactNode }) => {
      const child = React.Children.toArray(children)[0];
      if (React.isValidElement(child) && 
          (child.type === markdownComponents.networkgraph || 
           child.type === markdownComponents.plot ||
           child.type === markdownComponents.preview ||
           child.type === markdownComponents.googlesearch ||
           child.type === markdownComponents.alert ||
           child.type === markdownComponents.svg ||
           child.type === markdownComponents.toolconfirmation ||
           child.type === markdownComponents.assistantresponse ||
           child.type === markdownComponents.artifact)) {
        return <>{children}</>;
      }
      return <p>{children}</p>;
    },
  }), [role, additionalComponents]);

  // Copy handlers
  const handleCopyMarkdown = async () => {
    try {
      const success = await copyAsMarkdown(cleanedContent);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy markdown content:', err);
    }
  };

  const handleCopyRendered = async () => {
    try {
      // Use the ref instead of querySelector
      const messageContainer = messageRef.current;
      if (!messageContainer) {
        console.log('Message container not found, falling back to HTML copy');
        return handleCopyAsHTML();
      }

      const success = await copyContentToClipboard({
        messageContainer,
        cleanedContent,
        markdownComponents: components
      });

      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback to basic HTML copy
        handleCopyAsHTML();
      }
    } catch (err) {
      console.error('Failed to copy hybrid content:', err);
      // Fallback to basic HTML copy
      handleCopyAsHTML();
    }
  };


  const handleCopyAsHTML = async () => {
    try {
      // Convert markdown to HTML using a temporary container
      const ReactDOMServer = (await import('react-dom/server')).default;
      
      // Render the markdown content to HTML string
      const htmlContent = ReactDOMServer.renderToStaticMarkup(
        <div>
          {contentChunks.map((chunk, index) => (
            <Markdown
              key={index}
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeRaw, rehypeKatex]}
              components={components}
              skipHtml={false}
            >
              {chunk}
            </Markdown>
          ))}
        </div>
      );
      
      // Create a blob with the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 
        'text/html': blob, 
        'text/plain': new Blob([cleanedContent], { type: 'text/plain' }) 
      });
      
      await navigator.clipboard.write([clipboardItem]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy as HTML:', err);
      // Final fallback to copying markdown
      handleCopyMarkdown();
    }
  };

  // Get styles for current role
  const styles = roleStyles[role];

  return (
    <div className={twMerge("space-y-2", className)}>
      {/* Main content */}
      <div
        ref={messageRef}
        className={twMerge(
          'prose prose-sm max-w-none relative',
          'rounded-lg border p-3',
          styles.container,
          className
        )}
      >
        {/* Copy button - only show for assistant/bot messages with content */}
        {!isUser && cleanedContent.trim() && (
          <Button
            onClick={handleCopyRendered}
            variant="ghost"
            size="sm"
            className="copy-button absolute bottom-2 right-2 h-8 w-8 p-0 opacity-60 hover:opacity-100 transition-opacity"
            title="Copy formatted content (preserves visual appearance)"
          >
            {copied ? (
              <Check className="h-3 w-3 text-green-500" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        )}
        
        {contentChunks.map((chunk, index) => (
          <ChunkErrorBoundary key={`chunk-${index}`} index={index}>
            <StreamingChunk
              content={chunk}
              components={components}
              streaming={shouldStreamThisMessage}
              streamSpeed={streamSpeed}
              streamMode={streamMode}
              onStreamComplete={onStreamComplete}
              index={index}
              isCurrentlyStreaming={isCurrentlyStreaming}
            />
          </ChunkErrorBoundary>
        ))}
      </div>
    </div>
  );
};

export default MessageContent;